import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface SensitiveEmployeeData {
  tax_id?: string | null;
  social_security_number?: string | null;
  iban?: string | null;
  bic?: string | null;
  health_insurance_name?: string | null;
  health_insurance_number?: string | null;
  tax_id_masked?: string | null;
  social_security_number_masked?: string | null;
  iban_masked?: string | null;
}

interface RpcResponse {
  success: boolean;
  data: Record<string, string | null> | null;
}

export function useSensitiveData(employeeId: string) {
  const { userRole } = useAuth();
  const [sensitiveData, setSensitiveData] = useState<SensitiveEmployeeData>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isAdmin = userRole === 'admin';

  const loadSensitiveData = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true);
    setError(null);

    try {
      if (isAdmin) {
        const { data, error: rpcError } = await supabase
          .rpc('get_employee_sensitive_data', { p_employee_id: employeeId });

        if (rpcError) {
          setError(rpcError.message);
          return;
        }

        const response = data as unknown as RpcResponse;
        if (response?.success && response?.data) {
          setSensitiveData({
            tax_id: response.data.tax_id,
            social_security_number: response.data.social_security_number,
            iban: response.data.iban,
            bic: response.data.bic,
            health_insurance_name: response.data.health_insurance_name,
            health_insurance_number: response.data.health_insurance_number,
          });
        }
      } else {
        const { data, error: rpcError } = await supabase
          .rpc('get_employee_sensitive_data_masked', { p_employee_id: employeeId });

        if (rpcError) {
          setError(rpcError.message);
          return;
        }

        const response = data as unknown as RpcResponse;
        if (response?.success && response?.data) {
          setSensitiveData({
            tax_id_masked: response.data.tax_id_masked,
            social_security_number_masked: response.data.social_security_number_masked,
            iban_masked: response.data.iban_masked,
            health_insurance_name: response.data.health_insurance_name,
          });
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  }, [employeeId, isAdmin]);

  const saveSensitiveData = useCallback(async (data: SensitiveEmployeeData) => {
    if (!employeeId || !isAdmin) {
      setError("Only administrators can save sensitive data");
      return false;
    }

    setLoading(true);
    try {
      const { data: result, error: rpcError } = await supabase
        .rpc('save_employee_sensitive_data', {
          p_employee_id: employeeId,
          p_tax_id: data.tax_id || null,
          p_social_security_number: data.social_security_number || null,
          p_iban: data.iban || null,
          p_bic: data.bic || null,
          p_health_insurance_name: data.health_insurance_name || null,
          p_health_insurance_number: data.health_insurance_number || null,
        });

      if (rpcError) {
        setError(rpcError.message);
        return false;
      }

      const response = result as unknown as RpcResponse;
      if (response?.success) {
        await loadSensitiveData();
        return true;
      }
      return false;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
      return false;
    } finally {
      setLoading(false);
    }
  }, [employeeId, isAdmin, loadSensitiveData]);

  return {
    sensitiveData,
    setSensitiveData,
    loading,
    error,
    loadSensitiveData,
    saveSensitiveData,
    isAdmin,
    canViewFull: isAdmin,
  };
}
